import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { get } from '../api/client';
import type { LeadsPage, MtxOverview } from '../api/types';
import { useMe } from '../auth';
import { IconAlert, IconExternal } from '../components/icons';
import { PageHead, Spinner, timeAgo } from '../components/ui';
import { useT } from '../i18n';
import { LeadItem } from './Leads';

/** Portada del dashboard de MTX. */
export function Home() {
  const { t, tr } = useT();
  const { data: me } = useMe();
  const { data, isPending } = useQuery({ queryKey: ['overview'], queryFn: () => get<MtxOverview>('/admin/overview') });
  const latest = useQuery({ queryKey: ['leads', 'list', 'home'], queryFn: () => get<LeadsPage>('/admin/leads?status=inbox&pageSize=5') });

  return (
    <>
      <PageHead kicker={t('Panel')} title={t('Hola, {name}', { name: me?.name.split(' ')[0] ?? '' })} description={t('Lo que pasa hoy en el sitio de MTX.')}>
        {data?.siteUrl && (
          <a className="btn" href={data.siteUrl} target="_blank" rel="noreferrer">
            <IconExternal /> {t('Ver el sitio')}
          </a>
        )}
      </PageHead>

      {isPending || !data ? (
        <Spinner />
      ) : (
        <div className="stack">
          <div className="grid grid-4 stats">
            <Link to="/mensajes" className="panel stat">
              <span className="label">{t('Mensajes nuevos')}</span>
              <span className="value" style={{ color: data.leads.new ? 'var(--accent-text)' : undefined }}>
                {data.leads.new}
              </span>
              <span className="hint">{data.leads.lastAt ? t('Último: {when}', { when: timeAgo(data.leads.lastAt) }) : t('Todavía no llega ninguno')}</span>
            </Link>
            <Link to="/motos" className="panel stat">
              <span className="label">{t('Motos visibles')}</span>
              <span className="value">
                {data.models.visible}
                <span className="faint" style={{ fontSize: 18 }}> / {data.models.total}</span>
              </span>
              <span className="hint">{t('en el catálogo')}</span>
            </Link>
            <Link to="/puntos-de-venta" className="panel stat">
              <span className="label">{t('Puntos de venta')}</span>
              <span className="value">{data.dealers}</span>
              <span className="hint">{t('visibles en el mapa')}</span>
            </Link>
            <Link to="/publicar" className="panel stat">
              <span className="label">{t('Sitio MTX')}</span>
              <span className="value" style={{ fontSize: 20, marginTop: 6 }}>
                {data.publish.publishing ? (
                  <span className="badge info">{t('Publicando…')}</span>
                ) : data.publish.upToDate ? (
                  <span className="badge ok">{t('Al día')}</span>
                ) : (
                  <span className="badge warn">{t('Cambios sin publicar')}</span>
                )}
              </span>
              <span className="hint">{data.publish.publishedAt ? t('Publicado {when}', { when: timeAgo(data.publish.publishedAt) }) : t('Todavía no se publica')}</span>
            </Link>
            <div className="panel stat">
              <span className="label">{t('Avisos por correo')}</span>
              <span className="value" style={{ fontSize: 20, marginTop: 6 }}>
                {data.notifications ? <span className="badge ok">{t('Activos')}</span> : <span className="badge warn">{t('Sin configurar')}</span>}
              </span>
              <span className="hint">{data.notifications ? t('Cada mensaje nuevo llega al correo') : t('Los mensajes solo quedan aquí')}</span>
            </div>
          </div>

          {(data.models.withErrors.length > 0 || data.catalogIssues.length > 0) && (
            <div className="panel panel-pad">
              <h2>{t('Para revisar antes de publicar')}</h2>
              <p className="sub" style={{ margin: '2px 0 14px' }}>
                {t('Las motos visibles con datos incompletos no se pueden publicar.')}
              </p>
              <ul className="issues">
                {data.models.withErrors.map((m) => (
                  <li key={m.id}>
                    <IconAlert className="ico" style={{ color: 'var(--red)' }} />
                    <span>{tr('{name} tiene datos obligatorios sin completar.', { name: <Link to={`/motos/${m.id}`}>{m.name}</Link> })}</span>
                  </li>
                ))}
                {data.catalogIssues.map((i) => (
                  <li key={i.message}>
                    <IconAlert className="ico" style={{ color: i.level === 'error' ? 'var(--red)' : 'var(--warn)' }} />
                    <span>{i.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <section className="panel" style={{ overflow: 'hidden' }}>
            <div className="row between panel-pad" style={{ paddingBottom: 12 }}>
              <h2 style={{ margin: 0 }}>{t('Últimos mensajes')}</h2>
              <Link to="/mensajes" className="btn btn-sm btn-ghost">
                {t('Ver la bandeja')}
              </Link>
            </div>
            {latest.isPending ? (
              <div className="panel-pad" style={{ paddingTop: 0 }}>
                <Spinner />
              </div>
            ) : latest.data?.items.length ? (
              <div style={{ borderTop: '1px solid var(--line)' }}>
                {latest.data.items.map((l) => (
                  <LeadItem key={l.id} lead={l} to={`/mensajes/${l.id}`} />
                ))}
              </div>
            ) : (
              <div className="empty-state" style={{ paddingTop: 12 }}>
                {t('Todavía no llega ningún mensaje por los formularios.')}
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
